/**
 * Live Truck Status Component
 * Shows real-time status of all active trucks with SLA countdown
 */
import { Truck, Clock, AlertTriangle, CheckCircle, Loader } from 'lucide-react';

const LiveTruckStatus = ({ data, isFullScreen }) => {
  if (!data || !Array.isArray(data)) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <Truck className="w-6 h-6 mr-2 text-blue-600" />
          Live Truck Status
        </h2>
        <div className="text-center py-8 text-gray-500">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading truck status...</p>
        </div>
      </div>
    );
  }

  const getStateColor = (state) => {
    const colors = {
      ARRIVED: 'bg-blue-100 text-blue-800',
      GATE_CHECK: 'bg-yellow-100 text-yellow-800',
      DOCK_ASSIGNED: 'bg-purple-100 text-purple-800',
      UNLOADING: 'bg-orange-100 text-orange-800',
      QC_INSPECTION: 'bg-indigo-100 text-indigo-800',
      GRN_CREATION: 'bg-teal-100 text-teal-800',
      SAP_POSTED: 'bg-green-100 text-green-800',
      COMPLETED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
      ON_HOLD: 'bg-gray-100 text-gray-800',
      EXCEPTION: 'bg-red-100 text-red-800',
    };
    return colors[state] || 'bg-gray-100 text-gray-800';
  };

  const getSLAStatus = (sla_remaining) => {
    if (sla_remaining === null || sla_remaining === undefined) {
      return { color: 'text-gray-500', icon: Clock, label: 'N/A' };
    }
    if (sla_remaining < 0) {
      return { color: 'text-red-600', icon: AlertTriangle, label: 'BREACHED' };
    }
    if (sla_remaining < 30) {
      return { color: 'text-orange-600', icon: AlertTriangle, label: 'AT RISK' };
    }
    return { color: 'text-green-600', icon: CheckCircle, label: 'ON TIME' };
  };

  const formatTime = (minutes) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    if (minutes < 0) {
      const absMinutes = Math.abs(minutes);
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return `-${hours}h ${mins}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className={`font-bold text-gray-800 flex items-center ${isFullScreen ? 'text-lg' : 'text-xl'}`}>
          <Truck className="w-6 h-6 mr-2 text-blue-600" />
          Live Truck Status
        </h2>
        <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
          {data.length} Active
        </span>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {data.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No active trucks at the moment</p>
          </div>
        ) : (
          data.map((truck, index) => {
            const slaStatus = getSLAStatus(truck.sla_remaining);
            const SLAIcon = slaStatus.icon;

            return (
              <div
                key={truck.id || index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-bold text-gray-800 text-lg">
                        {truck.truck_number || 'N/A'}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStateColor(truck.current_state)}`}>
                        {truck.current_state_display || truck.current_state}
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center space-x-2">
                        <span>Workflow ID: {truck.workflow_id}</span>
                        {truck.material_type && (
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            truck.material_type === 'JIT' ? 'bg-red-100 text-red-700' :
                            truck.material_type === 'Critical' ? 'bg-orange-100 text-orange-700' :
                            truck.material_type === 'FOC' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {truck.material_type}
                          </span>
                        )}
                      </div>
                      {truck.supplier && <div>Supplier: {truck.supplier}</div>}
                      {truck.po_number && <div>PO: {truck.po_number}</div>}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{truck.progress || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${truck.progress || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* SLA Status */}
                  <div className="ml-4 text-right">
                    <div className={`flex items-center justify-end space-x-1 ${slaStatus.color} font-semibold text-sm mb-1`}>
                      <SLAIcon className="w-4 h-4" />
                      <span>{slaStatus.label}</span>
                    </div>
                    <div className={`text-lg font-bold ${slaStatus.color}`}>
                      {formatTime(truck.sla_remaining)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {truck.elapsed_time ? `Elapsed: ${formatTime(truck.elapsed_time)}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LiveTruckStatus;
